import { Component, Input, OnInit } from '@angular/core';
import { FormGroup } from '@angular/forms';
import { HotkeysService } from 'src/app/_services/hotkeys.service';
import { Hotkey } from 'src/app/_models/hotkey';
import { DynamicDialogConfig } from 'primeng/dynamicdialog';
import { FormBuilder } from '@angular/forms';
import { TechnicalViewService } from 'src/app/_services/technical-view.service';
import { MessageService } from 'primeng/api';
import { DynamicDialogRef } from 'primeng/dynamicdialog';

@Component({
  selector: 'app-job-form',
  templateUrl: './job-form.component.html',
  styleUrls: ['./job-form.component.scss'],
})
export class JobFormComponent implements OnInit {
  @Input() inputFormGroup: FormGroup | undefined;
  @Input() selectedHotkey: any;
  // Description = component suggestions; Remarks = detail suggestions for the
  // chosen component.
  suggestedDescriptions: string[] = [];
  suggestedRemarks: string[] = [];
  selectedIssues: any[] = [];
  loading = false;
  drafting = false;
  @Input() data: any;
  isEdit = false;

  constructor(
    private hotkeysService: HotkeysService,
    private config: DynamicDialogConfig,
    private fb: FormBuilder,
    public technicalViewService: TechnicalViewService,
    private messageService: MessageService,
    public ref: DynamicDialogRef
  ) {}

  jobForm = this.fb.group({
    job_description: [
      {
        value: '',
      },
    ],
    remarks: [''],
  });

  ngOnInit(): void {
    // console.log('job data', this.data);

    if (this.data) {
      this.isEdit = true;

      const technical_job = this.fb.group({
        job_description: [
          {
            value: this.data.job_description,
          },
        ],
        remarks: this.data.remarks,
        id: this.data.id,
      });
      // console.log('technical_job', technical_job);

      this.inputFormGroup?.patchValue(technical_job.value);
    }
  }

  private currentComponent(): string {
    const jd = this.inputFormGroup?.controls?.['job_description']?.value;
    return typeof jd === 'string' ? jd.trim() : String(jd?.value ?? '').trim();
  }

  // Description field: suggest components.
  filterDescriptions(event: any) {
    this.hotkeysService
      .getComponents(event.query)
      .subscribe((components) => (this.suggestedDescriptions = components));
  }

  // Remarks field: suggest the detail lines used for the chosen component.
  filterRemarks(event: any) {
    const component = this.currentComponent();
    if (!component) {
      this.suggestedRemarks = [];
      return;
    }
    this.hotkeysService
      .getDetailsForComponent(component, event.query)
      .subscribe((details) => (this.suggestedRemarks = details));
  }

  onDescriptionSelect(event: any) {
    // New component picked: clear stale remark suggestions.
    this.suggestedRemarks = [];
    this.selectedIssues.push(event);
  }

  private remarksControl() {
    return this.inputFormGroup?.controls?.['remarks'];
  }

  // "Draft" button: ask the AI module to suggest a Remarks detail for the
  // current component. Fills the Remarks field and offers the alternatives as
  // suggestions. Degrades gracefully - when no AI provider is configured the
  // backend returns the component's known details instead.
  draftRemark() {
    const component = this.currentComponent();
    if (!component) {
      this.messageService.add({
        severity: 'info',
        summary: 'Pick a component first',
        detail: 'Type or choose a Description, then draft a remark.',
        key: 'technical',
      });
      return;
    }
    this.drafting = true;
    this.hotkeysService.draftRemark(component).subscribe({
      next: (res) => {
        const remark = (res?.remark || '').trim();
        if (remark) {
          this.remarksControl()?.setValue(remark);
        }
        this.suggestedRemarks = [remark, ...(res?.alternatives || [])].filter(
          (v, i, a) => v && a.indexOf(v) === i
        );
        this.drafting = false;
      },
      error: () => {
        this.drafting = false;
      },
    });
  }

  updateJob() {
    this.loading = true;
    // console.log('this.jobForm.value', this.inputFormGroup?.value);

    const formData = this.inputFormGroup?.value; // Assuming `form` is your FormGroup

    // Normalise the description: it may be a plain typed string (free-form) or
    // a {value,label} suggestion object. Store a plain string either way so a
    // free-typed description is never dropped, then persist it as a suggestion.
    if (formData) {
      const jd = formData.job_description;
      formData.job_description =
        jd == null
          ? ''
          : typeof jd === 'string'
          ? jd.trim()
          : String(jd.value ?? jd.label ?? '').trim();
      this.inputFormGroup?.patchValue(formData);
      // Persist the component + detail so both reflect as suggestions later.
      this.hotkeysService.ensureShortcut(
        formData.job_description,
        (formData.remarks || '').trim()
      );
    }

    this.technicalViewService
      .updateTechnicalRecordJobs(this.inputFormGroup?.value, this.data.id)
      .subscribe(
        (response) => {
          this.technicalViewService
            .getTechnicalRecordsByVehicleId(this.data.id, 1, 1000)
            .subscribe((data: any) => {
              this.technicalViewService.updateTechnicalRecords(data.body);
              this.messageService.add({
                severity: 'success',
                summary: 'Success',
                detail: 'Job updated successfully',
                key: 'technical',
              });
              this.loading = false;
              this.ref.close(response);
            });
        },
        (error) => {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: 'Job update failed',
            key: 'technical',
          });
          this.loading = false;
        }
      );
  }
}
